import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  CollabEditorFlushResult,
  CollabEditorParticipant,
  CollabEditorPresence,
  CollabEditorSource,
  CollabEditorState,
  CollabEditorTermination,
  CollabEditorUser,
  CollabEditorWriteRejection,
} from '@nimbalyst/collab-bundle/editor';
import { encodeStateAsUpdate } from 'yjs';
import type { Doc } from 'yjs';
import '@nimbalyst/collab-bundle/styles.css';
import { BUTTON_PRIMARY_LG, BUTTON_SECONDARY_LG } from '../controlStyles';
import {
  EDITOR_LOADING,
  EDITOR_NOTICE_ERROR,
  EDITOR_NOTICE_LIMIT,
  EDITOR_STATUS_STRIP,
  EDITOR_SURFACE,
} from './editorSurfaceStyles';
import {
  CollabDocumentStatusIndicator,
  presentConnectionState,
  useCollabDocumentStatusSink,
  type BrowserConnectionState,
  type CollabDocumentSourceMode,
  type CollabDocumentStatus,
} from './documentStatus';

/**
 * Everything a mounted collaborative document needs from its host, whichever
 * editor is painting it.
 *
 * The Lexical editor and an extension-provided editor are mounted by different
 * bundle entry points and hold different handles, but the surface around them —
 * the connection state machine, the size ceiling, the deferred teardown flush,
 * the panels that explain a document that will not open — is identical, and was
 * written once at some cost. Two copies of it would drift, and the comments in
 * this file are a list of the ways it has already gone wrong.
 */

/**
 * Maximum collaborative state this browser editor will hold.
 *
 * Measured on the editor's own sync stream, once the room has spoken. A
 * separate probe socket used to stream the whole document ahead of the mount
 * purely to add its bytes up, so every open paid two full downloads back to
 * back — and the second could not even start until the first finished. The
 * ceiling is the same either way; only the room's answer is authoritative, so
 * only the room is asked, once.
 */
export const BROWSER_DOCUMENT_STATE_LIMIT_BYTES = 8 * 1024 * 1024;

/** The verdict when a document exceeds what this browser will hold. */
interface OversizedDocument {
  payloadBytes: number;
  maxBytes: number;
}

/**
 * How long a document may sit unsynced before the browser stops calling it
 * "Connecting" and calls it broken.
 *
 * Long enough to cover a slow room open and a couple of reconnect backoffs;
 * short enough that a writer is not left staring at an empty editable document
 * that will never receive content. The failure this bounds is real: a token
 * exchange that throws leaves the sync provider retrying behind exponential
 * backoff, and before that it left the provider dead with no retry at all.
 */
const FIRST_SYNC_DEADLINE_MS = 15_000;

/** The part of a mount handle this surface drives. */
export interface CollabSurfaceHandle {
  getDocument(): Doc;
  hasPendingWrites?(): boolean;
  getState(): CollabEditorState;
  /** Relabel this participant for the room without remounting. */
  setUser?(user: CollabEditorUser): void;
  flush(options?: { timeoutMs?: number }): Promise<CollabEditorFlushResult>;
  markClean(): void;
  destroy(): void;
}

/** What a mount strategy is handed. Mirrors both bundle mount contracts. */
export interface CollabSurfaceMountRequest {
  element: HTMLElement;
  source: CollabEditorSource;
  user: CollabEditorUser;
  onStateChange(state: CollabEditorState): void;
  onPresenceChange(presence: CollabEditorPresence): void;
  onWriteRejected(rejection: CollabEditorWriteRejection): void;
  onTermination(termination: CollabEditorTermination): void;
  /** The session failed. The editor stays on screen with an error above it. */
  onError(error: Error): void;
  /** The document arrived but did not render. The editor is hidden. */
  onBindingError(error: Error): void;
}

/**
 * Mount one editor onto `element`. Must be referentially stable: it is a mount
 * dependency, and a new identity tears down a live editor and destroys its
 * Y.Doc behind a flush.
 */
export type CollabSurfaceMount = (request: CollabSurfaceMountRequest) => CollabSurfaceHandle;

function isSuccessfulFlush(result: CollabEditorFlushResult): boolean {
  return result.status === 'acknowledged' || result.status === 'not-required';
}

/** One decimal at most, and never a trailing `.0` — the limit should read "8 MiB". */
function formatMebibytes(bytes: number): string {
  const mebibytes = bytes / (1024 * 1024);
  return `${Number(mebibytes.toFixed(1))} MiB`;
}

function flushFailureMessage(result: CollabEditorFlushResult): string {
  switch (result.status) {
    case 'rejected':
      return 'The server rejected the pending edit. The document is read-only and the rejected change was not saved.';
    case 'unavailable':
      if (result.reason === 'removed-from-org') return 'The pending edit was not saved because organization access was removed.';
      if (result.reason === 'document-access-revoked') return 'The pending edit was not saved because document access was removed.';
      if (result.reason === 'deleted-document') return 'The pending edit was not saved because the document was deleted.';
      if (result.reason === 'server-read-only') return 'The pending edit was not saved because the server made this document read-only.';
      return 'The pending edit could not be confirmed because the document connection was unavailable.';
    case 'timed-out':
      return `The server did not confirm the pending edit within ${result.timeoutMs.toLocaleString()} ms.`;
    case 'failed':
      return result.message;
    default:
      return '';
  }
}

/**
 * Teardown flushes that never came back confirmed, keyed by document.
 *
 * The flush that ends a mount outlives the component that started it: leaving
 * a document detaches the surface immediately and the flush settles after,
 * with `active` already false. Every failure on that path therefore had
 * nowhere to go but `console`, including a rejection, which used to skip the
 * recording path entirely. Holding it here lets the next mount of the same
 * document state plainly that an edit was never confirmed saved, rather than
 * opening clean over an edit the server may not have.
 */
const unconfirmedFlushes = new Map<string, string>();

/** Identifies the document a carried flush failure belongs to. */
function documentKey(source: CollabEditorSource): string | null {
  if (source.kind !== 'team-room') return null;
  return `${source.room.orgId}/${source.room.projectId}/${source.room.documentId}`;
}

/**
 * The identity a remount is actually about.
 *
 * A presence display name resolves asynchronously — the browser console shows
 * the member id until the roster request comes back — and that produced a new
 * `user` object. As a mount dependency it tore down a live editor and
 * destroyed its Y.Doc for a cosmetic relabel, in the middle of typing.
 */
function editorIdentityKey(user: CollabEditorUser): string {
  return user.memberId || user.name || '';
}

function terminationSurface(termination: CollabEditorTermination): {
  kind: 'removed-member' | 'no-access' | 'deleted-document';
  title: string;
  message: string;
} {
  if (termination.reason === 'deleted-document') {
    return {
      kind: 'deleted-document',
      title: 'Document not found',
      message: termination.message || 'This document was deleted and is no longer available.',
    };
  }
  if (termination.reason === 'removed-from-org') {
    return {
      kind: 'removed-member',
      title: 'Organization access was removed',
      message: termination.message || 'Your membership is no longer active. Live document access has been stopped.',
    };
  }
  return {
    kind: 'no-access',
    title: 'Document access was removed',
    message: termination.message || 'Your access to this document was revoked. Reconnecting will not restore it.',
  };
}

/**
 * How much of this surface's chrome belongs on screen.
 *
 * `page` is the document route: a status indicator, and a full explanatory
 * panel for every way an open can go wrong.
 *
 * `inline` is a preview or a popover — a card-sized box, or one artifact among
 * several being compared. The machinery is identical and deliberately shared
 * (this file's header says what two copies of it would cost), but page chrome
 * at that size is wrong rather than merely large: a status pill over a 128px
 * thumbnail is most of the thumbnail, and a "too large to edit in a browser"
 * panel with a desktop button is an answer to a question nobody asked of a
 * preview. Failures still surface, compactly.
 */
export type CollabDocumentPresentation = 'page' | 'inline';

export interface CollabDocumentSurfaceProps {
  source: CollabEditorSource;
  user: CollabEditorUser;
  desktopHref?: string;
  mount: CollabSurfaceMount;
  presentation?: CollabDocumentPresentation;
  /**
   * The live handle, or null once it has been released. Only callers that push
   * a late-resolving answer into a mounted editor need this.
   */
  onHandleChange?: (handle: CollabSurfaceHandle | null) => void;
  /**
   * The raw-source escape hatch, forwarded onto the published status so the
   * breadcrumb's existing Document actions menu can offer it. Only the
   * extension mount supplies one, and only for a document that can honour it.
   */
  sourceMode?: CollabDocumentSourceMode;
}

/** Host-owned DOM, lifecycle safety, and freshness chrome around one mounted editor. */
export function CollabDocumentSurface({
  source,
  user,
  desktopHref,
  mount,
  presentation = 'page',
  onHandleChange,
  sourceMode,
}: CollabDocumentSurfaceProps) {
  const inline = presentation === 'inline';
  const elementRef = useRef<HTMLDivElement>(null);
  /**
   * Held off the mount effect's dependencies: a caller that rebuilds this
   * callback on an incidental render must not tear down a live editor for it.
   */
  const onHandleChangeRef = useRef(onHandleChange);
  onHandleChangeRef.current = onHandleChange;
  /** The live handle, so a name that resolves after mount can reach it. */
  const liveHandleRef = useRef<CollabSurfaceHandle | null>(null);
  const [connection, setConnection] = useState<BrowserConnectionState>(
    source.kind === 'in-memory' ? 'local' : 'connecting',
  );
  const [readOnly, setReadOnlyPresentation] = useState(false);
  /**
   * Set only when the server did not confirm an edit.
   *
   * There is no counterpart for the healthy case. A collaborative edit is
   * streamed and acknowledged as it is typed, so "saved" is the resting state
   * and reporting it continuously says nothing; the only thing worth a word is
   * the moment that stops being true.
   */
  const [saveFailure, setSaveFailure] = useState<string | null>(null);
  const [participants, setParticipants] = useState<CollabEditorParticipant[]>([]);
  const [writeRejection, setWriteRejection] = useState<CollabEditorWriteRejection | null>(null);
  const [termination, setTermination] = useState<CollabEditorTermination | null>(null);
  const [oversized, setOversized] = useState<OversizedDocument | null>(null);
  const [error, setError] = useState<Error | null>(null);
  /**
   * Whether this document has ever completed a sync with the server.
   *
   * Until it has, the editor holds an EMPTY document — the browser passes
   * `deferInitialSync`/`shouldBootstrap: false` so the server stays
   * authoritative — and an empty rich-text document renders Lexical's
   * "Enter some rich text..." placeholder. Showing that is indistinguishable
   * from a genuinely blank shared document, and it is editable, so a writer can
   * type into a Y.Doc that will never reach anyone. The editor stays hidden
   * until the server has spoken once.
   */
  const [hasSynced, setHasSynced] = useState(source.kind === 'in-memory');
  /** Set when the first sync never arrived in time. */
  const [firstSyncFailed, setFirstSyncFailed] = useState(false);
  /**
   * The document arrived but the editor could not render it.
   *
   * Tracked apart from `firstSyncFailed` because the connection is healthy —
   * the sync gate above passes and would happily show the empty editor that
   * the aborted binding left behind, which is the same blank-document lie in a
   * different disguise.
   */
  const [bindingError, setBindingError] = useState<Error | null>(null);
  /** Bumped by Retry to force a full remount of the editor. */
  const [retryCount, setRetryCount] = useState(0);
  const userIdentity = editorIdentityKey(user);

  useEffect(() => {
    const container = elementRef.current;
    if (!container) return;

    /**
     * Every mount gets a private surface node; nothing ever mounts onto the
     * host container itself.
     *
     * The bundle mount owns its element through `createRoot(element)`, but
     * the container outlives an individual mount — opening a second document
     * re-runs this effect against the same node. The outgoing handle is not
     * destroyed at cleanup time: teardown is deferred behind a flush, and a
     * document the writer has typed in is dirty, so that flush is a server
     * round trip that routinely outlives the next document's mount. Two React
     * roots then shared one node: the newer root cleared the container on
     * first render, and the older root's late `unmount()` threw
     * `NotFoundError` mid-commit and took the live editor down with it — the
     * second document a user opened rendered permanently blank.
     *
     * A per-mount child, detached from the DOM the moment this effect is torn
     * down, means the two roots never meet regardless of which finishes first.
     */
    const element = container.ownerDocument.createElement('div');
    element.className = 'collab-editor-mount-surface';
    container.replaceChildren(element);

    let active = true;
    let hasBeenLive = false;
    let handle: CollabSurfaceHandle | null = null;
    let removeSizeListener: (() => void) | null = null;
    let activeFlush: Promise<CollabEditorFlushResult> | null = null;
    let sizeBaselineReady = false;
    let estimatedStateBytes = 0;
    let editRevision = 0;
    let autosave: ReturnType<typeof setTimeout> | null = null;
    // A previous mount of this document may have ended with an unconfirmed
    // flush. Take it once and say so, rather than opening clean.
    const key = documentKey(source);
    const carriedFailure = key ? unconfirmedFlushes.get(key) ?? null : null;
    if (key) unconfirmedFlushes.delete(key);
    setConnection(source.kind === 'in-memory' ? 'local' : 'connecting');
    setReadOnlyPresentation(false);
    setSaveFailure(carriedFailure);
    setParticipants([]);
    setWriteRejection(null);
    setTermination(null);
    setOversized(null);
    setError(null);
    setHasSynced(source.kind === 'in-memory');
    setFirstSyncFailed(false);
    setBindingError(null);

    const publishHandle = (next: CollabSurfaceHandle | null): void => {
      liveHandleRef.current = next;
      onHandleChangeRef.current?.(next);
    };

    // The deadline covers the whole open, not just the socket: token exchange,
    // room open and initial sync all have to land inside it.
    const firstSyncDeadline = source.kind === 'in-memory'
      ? null
      : setTimeout(() => {
        if (active) setFirstSyncFailed(true);
      }, FIRST_SYNC_DEADLINE_MS);
    const clearFirstSyncDeadline = (): void => {
      if (firstSyncDeadline !== null) clearTimeout(firstSyncDeadline);
    };

    const recordFlushResult = (
      result: CollabEditorFlushResult,
      currentHandle: CollabSurfaceHandle,
      flushedRevision: number,
    ): void => {
      if (isSuccessfulFlush(result)) {
        if (editRevision === flushedRevision) currentHandle.markClean();
        else scheduleAutosave();
        if (active) setSaveFailure(null);
        return;
      }
      if (result.status === 'rejected' && active) setWriteRejection(result.rejection);
      const message = flushFailureMessage(result);
      console.warn('[WebConsoleCollab] Document flush was not confirmed:', result);
      if (active) setSaveFailure(message);
    };

    const flush = (timeoutMs: number): Promise<CollabEditorFlushResult> => {
      if (!handle) return Promise.resolve({ status: 'unavailable', reason: 'not-mounted' });
      if (activeFlush) return activeFlush;
      const currentHandle = handle;
      const flushedRevision = editRevision;
      activeFlush = currentHandle.flush({ timeoutMs }).then((result) => {
        recordFlushResult(result, currentHandle, flushedRevision);
        return result;
      }).catch((cause): CollabEditorFlushResult => {
        const failed = {
          status: 'failed' as const,
          message: cause instanceof Error ? cause.message : 'The document flush failed.',
        };
        recordFlushResult(failed, currentHandle, flushedRevision);
        return failed;
      }).finally(() => {
        activeFlush = null;
      });
      return activeFlush;
    };

    const flushIfDirty = (timeoutMs: number): Promise<CollabEditorFlushResult> => {
      if (!handle || handle.getState().edit !== 'dirty') {
        return Promise.resolve({ status: 'not-required', reason: 'empty-document' });
      }
      return flush(timeoutMs);
    };

    function scheduleAutosave(): void {
      if (!active) return;
      if (autosave) clearTimeout(autosave);
      autosave = setTimeout(() => { autosave = null; void flushIfDirty(5_000); }, 500);
    }
    const onBeforeUnload = (event: BeforeUnloadEvent): void => {
      if (!handle || !(handle.hasPendingWrites?.() ?? handle.getState().edit === 'dirty')) return;
      event.preventDefault();
      event.returnValue = '';
      void flushIfDirty(1_500);
    };
    const onPageHide = (): void => {
      void flushIfDirty(1_500);
    };
    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'hidden') void flushIfDirty(3_000);
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibilityChange);

    const mountEditor = (): void => {
      const onStateChange = (state: CollabEditorState) => {
        if (!active) return;
        if (state.edit === 'dirty') scheduleAutosave();
        if (state.connection === 'connected') {
          hasBeenLive = true;
          // 'connected' is reported only after the server's initial sync
          // response has been applied, so this is the first moment the editor
          // is showing real document content rather than an empty doc.
          clearFirstSyncDeadline();
          setHasSynced(true);
          setFirstSyncFailed(false);
        }
        setConnection(presentConnectionState(state, hasBeenLive));
        setReadOnlyPresentation(state.serverAccess === 'read-only' || state.serverAccess === 'revoked');
        if (state.connection === 'connected' && handle && !sizeBaselineReady) {
          // The room has finished its initial sync, so this is the whole
          // document — the same total the probe socket used to download
          // separately, read off the stream that had to arrive anyway.
          estimatedStateBytes = encodeStateAsUpdate(handle.getDocument()).byteLength;
          sizeBaselineReady = true;
          if (estimatedStateBytes > BROWSER_DOCUMENT_STATE_LIMIT_BYTES) {
            setOversized({
              payloadBytes: estimatedStateBytes,
              maxBytes: BROWSER_DOCUMENT_STATE_LIMIT_BYTES,
            });
            handle.destroy();
            handle = null;
            publishHandle(null);
          }
        }
        // `state.edit` is deliberately not surfaced. It latches to 'dirty' on
        // the first keystroke and clears only on an explicit flush, so it is
        // the right question to ask before tearing a mount down and the wrong
        // one to show a reader: it reported "Unsaved changes" for the entire
        // session on a document the server had already accepted every edit of.
      };
      const onWriteRejected = (rejection: CollabEditorWriteRejection) => {
        if (!active) return;
        setWriteRejection(rejection);
        setSaveFailure('The server rejected this edit. The rejected change was not saved.');
      };
      const onTermination = (nextTermination: CollabEditorTermination) => {
        if (!active) return;
        setTermination(nextTermination);
        setReadOnlyPresentation(true);
        setConnection('read-only');
      };

      try {
        handle = mount({
          element,
          source,
          user,
          onStateChange,
          onPresenceChange: (presence) => {
            if (active) setParticipants(presence.participants);
          },
          onWriteRejected,
          onTermination,
          onError: setError,
          onBindingError: (cause) => {
            if (active) setBindingError(cause);
          },
        });
        publishHandle(handle);
        const document = handle.getDocument();
        const onDocumentUpdate = (update: Uint8Array) => {
          editRevision += 1;
          if (handle?.getState().edit === 'dirty') scheduleAutosave();
          if (!active || !handle || !sizeBaselineReady) return;
          estimatedStateBytes += update.byteLength;
          if (estimatedStateBytes <= BROWSER_DOCUMENT_STATE_LIMIT_BYTES) return;
          const bytes = encodeStateAsUpdate(document).byteLength;
          estimatedStateBytes = bytes;
          if (bytes <= BROWSER_DOCUMENT_STATE_LIMIT_BYTES) return;
          setOversized({ payloadBytes: bytes, maxBytes: BROWSER_DOCUMENT_STATE_LIMIT_BYTES });
          removeSizeListener?.();
          removeSizeListener = null;
          handle.destroy();
          handle = null;
          publishHandle(null);
        };
        document.on('update', onDocumentUpdate);
        removeSizeListener = () => document.off('update', onDocumentUpdate);
      } catch (cause) {
        if (active) setError(cause instanceof Error ? cause : new Error('The collaborative editor could not be mounted.'));
      }
    };

    mountEditor();

    return () => {
      active = false;
      clearFirstSyncDeadline();
      // Detach before the deferred destroy so the outgoing editor leaves the
      // screen with the route rather than lingering until its flush settles,
      // and so its `root.unmount()` runs against a node no one else owns.
      element.remove();
      window.removeEventListener('beforeunload', onBeforeUnload);
      if (autosave) clearTimeout(autosave);
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      removeSizeListener?.();
      const currentHandle = handle;
      handle = null;
      publishHandle(null);
      if (!currentHandle) return;
      const flushedRevision = editRevision;
      const pending = currentHandle.getState().edit === 'dirty'
        ? currentHandle.flush({ timeoutMs: 5_000 })
        : Promise.resolve<CollabEditorFlushResult>({ status: 'not-required', reason: 'empty-document' });
      void pending
        // A rejection here used to reach only `console.warn`, skipping the
        // recording path entirely. Normalize it into the same result shape so
        // one branch below decides what happens to a possibly-lost edit.
        .catch((cause): CollabEditorFlushResult => ({
          status: 'failed',
          message: cause instanceof Error ? cause.message : 'The document flush failed.',
        }))
        .then((result) => {
          recordFlushResult(result, currentHandle, flushedRevision);
          if (isSuccessfulFlush(result)) return;
          console.error('[WebConsoleCollab] A pending edit was not confirmed saved before teardown:', result);
          if (key) {
            unconfirmedFlushes.set(
              key,
              `An edit you made before leaving this document was not confirmed saved. ${flushFailureMessage(result)}`,
            );
          }
        })
        .finally(() => currentHandle.destroy());
    };
    // `user` is deliberately absent: only `userIdentity` may remount. A
    // presence display name that resolves a request later is cosmetic, and
    // remounting for it destroys a live editor's Y.Doc behind a flush that can
    // fail. The `user` read at mount time is this render's object, which is the
    // one the mount should use.
  }, [mount, source, userIdentity, retryCount]);

  // The other half of keeping `user` out of the mount: a display name that
  // resolves after mount relabels this participant in place. Without it the
  // room kept whatever name the editor mounted with for the whole session.
  useEffect(() => {
    liveHandleRef.current?.setUser?.(user);
  }, [user]);

  const terminatedSurface = termination ? terminationSurface(termination) : null;
  /**
   * A document that never synced is only reported as such when nothing more
   * specific already explains it. Terminations and oversized documents render
   * their own panel and would otherwise be joined by a redundant second one.
   */
  const explainedByAnotherPanel = Boolean(oversized || terminatedSurface);
  const showBindingFailure = Boolean(bindingError) && !explainedByAnotherPanel;
  const showFirstSyncFailure = !hasSynced && firstSyncFailed
    && !explainedByAnotherPanel && !showBindingFailure;
  const showFirstSyncPending = !hasSynced && !firstSyncFailed && !bindingError
    && !explainedByAnotherPanel;
  // A document that did not render is not a document the user may edit. The
  // aborted binding leaves an empty editor surface behind, and showing it
  // invites edits into a document whose real content never loaded. An
  // oversized one is torn down the moment it is measured, so its surface is
  // just as empty.
  const editorVisible = hasSynced && !bindingError && !oversized;
  /**
   * `presentConnectionState` reports a never-live document as "Connecting"
   * because, until the deadline passes, it still might be. Past the deadline
   * that label is a lie — it claimed progress on a connection that had, in the
   * token-exchange failure case, stopped being attempted at all.
   */
  const displayedConnection: BrowserConnectionState = showFirstSyncFailure ? 'offline' : connection;

  /**
   * The status this document reports, published to whoever is hosting it.
   *
   * The shell renders it beside the breadcrumb, which is where the desktop app
   * puts the same two facts. A standalone harness route has no shell, so it
   * falls back to the strip below.
   */
  const publishStatus = useCollabDocumentStatusSink();
  const status = useMemo<CollabDocumentStatus>(() => ({
    connection: displayedConnection,
    readOnly,
    participants,
    ...(desktopHref ? { desktopHref } : {}),
    ...(sourceMode ? { sourceMode } : {}),
  }), [desktopHref, displayedConnection, participants, readOnly, sourceMode]);
  useEffect(() => {
    publishStatus?.(status);
    // Clearing on unmount matters: the shell outlives the editor, and a stale
    // "Live" beside the breadcrumb of a document that has gone away is a lie.
    return () => publishStatus?.(null);
  }, [publishStatus, status]);

  return (
    <section
      className={inline ? `${EDITOR_SURFACE} collab-editor-surface--inline` : EDITOR_SURFACE}
      data-connection-state={displayedConnection}
      data-read-only={readOnly}
      data-presentation={presentation}
    >
      {/* Only when nothing else is showing it. The shell hosts this beside the
          breadcrumb; the harness routes have no breadcrumb to host it. */}
      {!publishStatus && !inline && (
        <div className={EDITOR_STATUS_STRIP}>
          <CollabDocumentStatusIndicator status={status} />
        </div>
      )}
      {inline && (oversized || error || (showBindingFailure && bindingError)) && (
        /*
         * One compact line for every way an inline mount can fail, because at
         * this size the distinctions do not help: the reader is looking at a
         * preview of something else, and a paragraph explaining browser state
         * limits is longer than the box it appears in. Which failure it was is
         * still on the element for anyone debugging.
         */
        <div
          className="collab-editor-inline-unavailable"
          role="alert"
          data-docs-state={oversized ? 'document-too-large' : error ? 'error' : 'render-failed'}
        >
          This document cannot be shown here.
        </div>
      )}
      {oversized && !inline && (
        <div className={`docs-state-panel collab-editor-limit ${EDITOR_NOTICE_LIMIT}`} role="alert" data-docs-state="document-too-large">
          <strong>This document is too large to edit in a browser</strong>
          <span>
            Nothing is wrong with it and nothing has been lost — a browser tab
            cannot hold this much collaborative state safely. Nimbalyst desktop
            opens it with no limit.
          </span>
          {/* The measurement, not just the rule. Without it the panel asserts a
              verdict the user has no way to sanity-check. */}
          <span className="collab-editor-limit-size text-[0.78rem] tabular-nums text-nim-faint">
            {formatMebibytes(oversized.payloadBytes)} of {formatMebibytes(oversized.maxBytes)} browser limit
          </span>
          {desktopHref && <a className={`${BUTTON_PRIMARY_LG} mt-[0.4rem]`} href={desktopHref}>Open in Nimbalyst desktop</a>}
        </div>
      )}
      {terminatedSurface && termination && (
        <div className={`docs-state-panel collab-editor-error ${EDITOR_NOTICE_ERROR}`} role="alert" data-docs-state={terminatedSurface.kind}>
          <strong>{terminatedSurface.title}</strong>
          <span>{terminatedSurface.message}</span>
        </div>
      )}
      {writeRejection && (
        <div className={`docs-state-panel collab-editor-read-only ${EDITOR_NOTICE_ERROR} [&>small]:font-mono [&>small]:text-nim-muted`} role="alert" data-docs-state="read-only" data-client-update-id={writeRejection.clientUpdateId}>
          <strong>This document is now read-only</strong>
          <span>The server rejected this edit; the rejected change was not saved.</span>
          {writeRejection.clientUpdateId && <small>Edit reference: {writeRejection.clientUpdateId}</small>}
        </div>
      )}
      {saveFailure && !writeRejection && (
        <div className={`docs-state-panel collab-editor-error ${EDITOR_NOTICE_ERROR}`} role="alert" data-docs-state="save-failed">
          <strong>Save not confirmed</strong>
          <span>{saveFailure}</span>
        </div>
      )}
      {error && !inline && (
        <div className={`docs-state-panel collab-editor-error ${EDITOR_NOTICE_ERROR}`} role="alert">
          <strong>Shared document editor unavailable</strong>
          <span>{error.message}</span>
        </div>
      )}
      {/* No `role="status"`: the connection pill above is already the live
          region for this, and a second one announces the same state twice. */}
      {showBindingFailure && bindingError && !inline && (
        <div className={`docs-state-panel collab-editor-error ${EDITOR_NOTICE_ERROR} [&>small]:text-nim-muted`} role="alert" data-docs-state="render-failed">
          <strong>This document could not be displayed</strong>
          {/* Retry is deliberately absent: the document arrived intact, and
              this browser build cannot render it. Reloading produces the same
              result, so the honest way forward is the desktop app. */}
          <span>
            The document loaded but could not be displayed in this browser, so
            it is shown read-only rather than as an empty document. Nothing has
            been lost — Nimbalyst desktop opens it normally.
          </span>
          <small>{bindingError.message}</small>
          {desktopHref && <a className={`${BUTTON_PRIMARY_LG} mt-[0.4rem]`} href={desktopHref}>Open in Nimbalyst desktop</a>}
        </div>
      )}
      {showFirstSyncPending && (
        <div className={EDITOR_LOADING}>Opening this document…</div>
      )}
      {showFirstSyncFailure && (
        <div className={`docs-state-panel collab-editor-error ${EDITOR_NOTICE_ERROR}`} role="alert" data-docs-state="never-connected">
          <strong>This document could not be opened</strong>
          {/* Say what was NOT done, explicitly. The old behaviour showed an
              empty editable document here, which read as "this document is
              blank" and accepted edits that could never be delivered. */}
          <span>
            Nimbalyst could not reach the collaboration service, so this
            document&apos;s contents have not been loaded. Nothing has been lost
            and nothing you type here would be saved, so editing stays disabled
            until the connection is restored.
          </span>
          <button
            type="button"
            className={`collab-editor-retry ${BUTTON_PRIMARY_LG} mt-[0.4rem]`}
            onClick={() => {
              setFirstSyncFailed(false);
              setRetryCount(count => count + 1);
            }}
          >
            Try again
          </button>
          {desktopHref && <a className={`${BUTTON_SECONDARY_LG} mt-[0.4rem]`} href={desktopHref}>Open in Nimbalyst desktop</a>}
        </div>
      )}
      <div className="collab-editor-mount min-h-0 flex-1 overflow-hidden" ref={elementRef} hidden={!editorVisible} />
    </section>
  );
}
